import React, { useEffect, useState } from "react";
import {
  Accordion,
  Button,
  Checkbox,
  FormField,
  Grid,
  Icon,
  Label,
} from "semantic-ui-react";
import { Form, Formik } from "formik";
import searchIcon from "../../../assets/icons/accent-search-button.svg";
import { useDispatch, useSelector } from "react-redux";
import MyTransparentTextInput from "../../../app/common/form/MyTransparentTextInput";
import Tags from "../../../app/common/components/Tags";
import {
  dataFromSnapshot,
  deleteDocFromFirestore,
  deletePersonalDataFromJobInFirestore,
  fetchCompanyUsingRefFromFirestore,
  fetchJobUsingRefFromFirestore,
  fetchUserDataFromFirestore,
  listenToDocFromFirestore,
  updateDocument,
  toggleSettingInDocument,
} from "../../../app/firestore/firestoreService";
import ManageJobPosts from "../../employer/components/ManageJobPosts";
import isEmpty from "lodash/isEmpty";
import { toast } from "react-toastify";
import {
  asyncActionFinish,
  asyncActionStart,
} from "../../../app/async/asyncReducer";
import { jobDataFromSnapshotDoc } from "../../../app/firestore/helpers/jobDataFromSnapshotDoc";
import { EMPLOYER_USER } from "../../../app/firestore/firebaseService";
import {
  fetchCustomerDetails,
  fetchEmployerDraftJobs,
  fetchEmployerFreeCompanyFeaturedLogos,
  fetchEmployerFreeJobs,
  fetchEmployerPaidCompanyFeaturedLogos,
  fetchEmployerPaidJobs,
} from "../../employer/employerActions";
import ManagePromotionServices from "../../employer/components/ManagePromotionServices";
import { getDoc, getDocs } from "firebase/firestore";
import {
  FIRESTORE_COMPANY_PROFILE_COLLECTION,
  FIRESTORE_CANDIDATE_PROFILES_COLLECTION,
  FIRESTORE_DOCUMENT_FIELDS,
  FIRESTORE_USERS_COLLECTION,
  FIRESTORE_DRAFT_JOBS_COLLECTION,
  FIRESTORE_APPLICATIONS_COLLECTION,
  FIRESTORE_FEATURED_COMPANY_LOGOS_COLLECTION,
  FIRESTORE_COMPANIES_COLLECTION,
  FIRESTORE_TEAMS_COLLECTION,
  QUERY_CODES,
} from "../../../app/firestore/firestoreConstants";
import DiscountMode from "../../employer/components/DiscountMode";
import {
  callAPI,
  DELETE_USER_ENDPOINT,
  GET_AUTH_DETAILS_ENDPOINT,
} from "../../../app/api/callAPI";
import Divider from "../../../app/common/components/Divider";
import UxToggle from "../../../app/common/components/UxToggle";
import CompanyProfileListItem from "./CompanyProfileListItem";
import useFirestoreDoc from "../../../app/hooks/useFirestoreDoc";
import { getJobSubmittedDate } from "../../../js/helpers/getItemDisplayDate";
import { getDateAndTime } from "../../../js/helpers/dateHelpers";
import ProfileSuggestionCard from "../../candidates/components/ProfileSuggestionCard";
import cuid from "cuid";

const LOOKUP_EMAIL_VALUE = "email";
const LOOKUP_ID_VALUE = "id";
const LOOKUP_JOB_REF_VALUE = "jobRef";
const LOOKUP_COMPANY_REF_VALUE = "companyRef";
const LOOKUP_APPLICATION_VALUE = "application";

const lookupTypes = {
  email: "User email",
  id: "User Id",
  jobRef: "Job REF number",
  companyRef: "Company REF name",
  application: "Application Id",
};

export default function LookupPane() {
  const dispatch = useDispatch();

  const [formValue, setFormValue] = useState("");
  const [formError, setFormError] = useState(false);
  const [isConfirmDelete, setIsConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [account, setAccount] = useState(null);
  const [authDetails, setAuthDetails] = useState(null);
  const [refJob, setRefJob] = useState(null);
  const [error, setError] = useState(null);
  const [isManageAccountAccordionOpen, setIsManageAccountAccordionOpen] =
    useState(false);
  const [isAuthAccordionOpen, setIsAuthAccordionOpen] = useState(false);
  const [searchType, setSearchType] = useState(LOOKUP_EMAIL_VALUE);
  const [isAccountSettingsLocked, setIsAccountSettingsLocked] = useState(true);
  const [company, setCompany] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [application, setApplication] = useState(null);

  const { jobs, draftJobs, companyFeaturedLogos } = useSelector(
    (state) => state.employer
  );

  const isEmployer = !!account?.isEmployer;
  const isCompanyOwnerAdmin =
    isEmployer && company && account && company.employerId === account.id;
  const isCandidate = !!(account?.isJobSeeker && account?.candidateProfileId);

  useFirestoreDoc({
    shouldExecute: isEmployer && !!account?.companyId,
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_COMPANIES_COLLECTION,
        id: account?.companyId,
      }),
    data: (comp) => setCompany({ ...comp, id: account?.companyId }),
    deps: [dispatch, isEmployer, account?.companyId],
    queryCode: QUERY_CODES.listenToCompanyProfileFromFirestore,
  });

  useFirestoreDoc({
    shouldExecute:
      searchType !== LOOKUP_COMPANY_REF_VALUE &&
      isEmployer &&
      !!company?.companyProfileId,
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_COMPANY_PROFILE_COLLECTION,
        id: company?.companyProfileId,
      }),
    data: (profile) =>
      setCompanyProfile({ ...profile, id: company?.companyProfileId }),
    deps: [dispatch, isEmployer, company?.companyProfileId],
    queryCode: QUERY_CODES.listenToCompanyProfileFromFirestore,
  });

  useFirestoreDoc({
    shouldExecute: isCandidate,
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_CANDIDATE_PROFILES_COLLECTION,
        id: account?.candidateProfileId,
      }),
    data: (profile) => setCandidateProfile(profile),
    deps: [isCandidate, account?.companyProfileId],
    queryCode: QUERY_CODES.listenToCandidateProfile,
  });

  useEffect(() => {
    if (account) {
      callAPI({
        endpoint: GET_AUTH_DETAILS_ENDPOINT,
        opts: {
          body: { id: account.id },
        },
      })
        .then((r) => {
          setAuthDetails(JSON.stringify(r.authDetails, null, 2));
        })
        .catch((e) => toast.error("Cannot fetch auth details", e.toString()));
    }
  }, [account]);

  function handleInputChange(e) {
    setFormValue(e.target.value);
    if (isEmpty(e.target.value)) handleClearButton();
  }

  function handleClearButton() {
    setFormValue("");
    setAccount(null);
    setAuthDetails(null);
    setFormError(false);
    setRefJob(null);
    setCompanyProfile(null);
    setCandidateProfile(null);
    setApplication(null);
    dispatch(fetchEmployerPaidJobs([]));
    dispatch(fetchEmployerFreeJobs([]));
    dispatch(fetchEmployerFreeCompanyFeaturedLogos([]));
    dispatch(fetchEmployerPaidCompanyFeaturedLogos([]));
    dispatch(fetchEmployerDraftJobs([]));
    dispatch(fetchCustomerDetails({ subscription: null, discount: null }));
  }

  function handleSearchTypeChange(e, { value }) {
    setSearchType(value);
    if (searchType !== value) {
      handleClearButton();
    }
  }

  function handleNoResultsFound() {
    dispatch(asyncActionFinish());
    setFormError(true);
  }

  function handleConfirmDeleteToggleChange(e, { checked }) {
    setIsConfirmDelete(checked);
  }

  async function deleteLookupUser() {
    try {
      setIsDeleting(true);

      console.log("1. Will Delete", account);
      console.log("2. Type:", account.type);
      console.log("3. isCompanyOwnerAdmin:", isCompanyOwnerAdmin, {
        account,
        company,
        companyProfile,
      });

      if (isCompanyOwnerAdmin) {
        console.log("4. Company Owner Admin");
        if (jobs.length > 0) {
          console.log("will delete jobs");
          for (const job of jobs) {
            await deletePersonalDataFromJobInFirestore(job, "");
          }
          console.log("jobs deleted");
        }

        if (draftJobs.length > 0) {
          console.log("will delete draft jobs");
          draftJobs.forEach((job) => {
            deleteDocFromFirestore({
              dbCollection: FIRESTORE_DRAFT_JOBS_COLLECTION,
              id: job.id,
            });
          });
          console.log("draft jobs deleted");
        }

        if (companyFeaturedLogos.length > 0) {
          console.log("will delete CLFs");
          companyFeaturedLogos.forEach((clf) => {
            deleteDocFromFirestore({
              dbCollection: FIRESTORE_FEATURED_COMPANY_LOGOS_COLLECTION,
              id: clf.id,
            });
          });
          console.log("CLFs deleted");
        }

        if (companyProfile) {
          console.log("will delete Company Profile", companyProfile.id);
          deleteDocFromFirestore({
            dbCollection: FIRESTORE_COMPANY_PROFILE_COLLECTION,
            id: companyProfile.id,
          });
          console.log("Company Profile deleted");
        }

        if (company) {
          console.log("will delete Company", company.id);
          deleteDocFromFirestore({
            dbCollection: FIRESTORE_COMPANIES_COLLECTION,
            id: company.id,
          });
          console.log("Company deleted");

          console.log("will delete Team", company.id);
          deleteDocFromFirestore({
            dbCollection: FIRESTORE_TEAMS_COLLECTION,
            id: company.id,
          });
          console.log("Team deleted");
        }
      }

      setCandidateProfile(null);

      // delete server-side first using id
      const deletedRes = await callAPI({
        endpoint: DELETE_USER_ENDPOINT,
        opts: {
          body: {
            userId: account.id,
          },
        },
      });

      if (deletedRes.body === "user deleted") {
        toast(`${account.id} deleted`, {
          position: "top-right",
          autoClose: 10000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });

        handleClearButton();
      }
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleConfirmApplication() {
    await updateDocument({
      dbCollection: FIRESTORE_APPLICATIONS_COLLECTION,
      id: application.id,
      data: {
        isAdminReviewed: true,
      },
    });
  }

  async function getUserFromId(userId) {
    await getDoc(
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_USERS_COLLECTION,
        id: userId,
      })
    )
      .then((doc) => {
        if (doc.exists) {
          setAccount({
            ...doc.data(),
            id: userId,
          });
        } else {
          handleNoResultsFound();
        }
        dispatch(asyncActionFinish());
      })
      .catch((error) => {
        console.log(error);
        throw error;
      });
  }

  if (account && account.isEmployer && !company) {
    return null;
  }

  return (
    <Grid className="body-blue-text">
      <Grid.Row columns={3}>
        <Grid.Column width={4}>
          <div className="body-bold-text">Search input type</div>
          <Checkbox
            style={{ display: "block", marginTop: 15 }}
            radio
            name="checkboxRadioGroup"
            value={LOOKUP_EMAIL_VALUE}
            checked={searchType === LOOKUP_EMAIL_VALUE}
            onChange={handleSearchTypeChange}
            label={{
              children: (
                <span className="body-blue-text">
                  {lookupTypes[LOOKUP_EMAIL_VALUE]}
                </span>
              ),
            }}
          />
          <Checkbox
            style={{ display: "block", marginTop: 4 }}
            radio
            name="checkboxRadioGroup"
            value={LOOKUP_ID_VALUE}
            checked={searchType === LOOKUP_ID_VALUE}
            onChange={handleSearchTypeChange}
            label={{
              children: (
                <span className="body-blue-text">
                  {lookupTypes[LOOKUP_ID_VALUE]}
                </span>
              ),
            }}
          />
          <Checkbox
            style={{ display: "block", marginTop: 4 }}
            radio
            name="checkboxRadioGroup"
            value={LOOKUP_JOB_REF_VALUE}
            checked={searchType === LOOKUP_JOB_REF_VALUE}
            onChange={handleSearchTypeChange}
            label={{
              children: (
                <span className="body-blue-text">
                  {lookupTypes[LOOKUP_JOB_REF_VALUE]}
                </span>
              ),
            }}
          />
          <Checkbox
            style={{ display: "block", marginTop: 4 }}
            radio
            name="checkboxRadioGroup"
            value={LOOKUP_COMPANY_REF_VALUE}
            checked={searchType === LOOKUP_COMPANY_REF_VALUE}
            onChange={handleSearchTypeChange}
            label={{
              children: (
                <span className="body-blue-text">
                  {lookupTypes[LOOKUP_COMPANY_REF_VALUE]}
                </span>
              ),
            }}
          />
          <Checkbox
            style={{ display: "block", marginTop: 4 }}
            radio
            name="checkboxRadioGroup"
            value={LOOKUP_APPLICATION_VALUE}
            checked={searchType === LOOKUP_APPLICATION_VALUE}
            onChange={handleSearchTypeChange}
            label={{
              children: (
                <span className="body-blue-text">
                  {lookupTypes[LOOKUP_APPLICATION_VALUE]}
                </span>
              ),
            }}
          />
        </Grid.Column>
        <Grid.Column style={{ marginTop: 45 }} width={8}>
          <Formik
            initialValues={{ formInput: "" }}
            onSubmit={async (values, { setSubmitting, setErrors }) => {
              setFormError(false);
              try {
                if (searchType === LOOKUP_EMAIL_VALUE) {
                  dispatch(asyncActionStart());
                  await getDocs(fetchUserDataFromFirestore(formValue))
                    .then((querySnapshot) => {
                      if (querySnapshot.docs.length === 0) {
                        handleNoResultsFound();
                        return;
                      }
                      querySnapshot.forEach((doc) => {
                        setAccount({
                          ...doc.data(),
                          id: doc.id,
                        });
                      });
                      dispatch(asyncActionFinish());
                    })
                    .catch((error) => {
                      console.log("Error getting documents: ", error);
                    });
                }

                if (searchType === LOOKUP_ID_VALUE) {
                  dispatch(asyncActionStart());
                  await getDoc(
                    listenToDocFromFirestore({
                      dbCollection: FIRESTORE_USERS_COLLECTION,
                      id: formValue,
                    })
                  )
                    .then((doc) => {
                      if (doc.exists) {
                        setAccount({
                          ...doc.data(),
                          id: doc.id,
                        });
                      } else {
                        handleNoResultsFound();
                      }
                      dispatch(asyncActionFinish());
                    })
                    .catch((error) => {
                      throw error;
                    });
                }

                if (searchType === LOOKUP_JOB_REF_VALUE) {
                  dispatch(asyncActionStart());
                  let userId;
                  await getDocs(fetchJobUsingRefFromFirestore(formValue))
                    .then((querySnapshot) => {
                      if (querySnapshot.docs.length === 0) {
                        handleNoResultsFound();
                        return;
                      }
                      querySnapshot.forEach((doc) => {
                        const job = jobDataFromSnapshotDoc(doc.id, doc.data());
                        console.log("job", job);
                        setRefJob(job);
                        userId = doc.data().employerId;
                      });
                      dispatch(asyncActionFinish());
                    })
                    .catch((error) => {
                      throw error;
                    });

                  await getUserFromId(userId);
                }

                if (searchType === LOOKUP_COMPANY_REF_VALUE) {
                  dispatch(asyncActionStart());
                  let userId;
                  await getDocs(fetchCompanyUsingRefFromFirestore(formValue))
                    .then((querySnapshot) => {
                      if (querySnapshot.docs.length === 0) {
                        handleNoResultsFound();
                        return;
                      }
                      querySnapshot.forEach((doc) => {
                        setCompanyProfile(dataFromSnapshot(doc));
                        userId = doc.data().employerId;
                      });
                      dispatch(asyncActionFinish());
                    })
                    .catch((error) => {
                      throw error;
                    });

                  await getUserFromId(userId);
                }

                if (searchType === LOOKUP_APPLICATION_VALUE) {
                  dispatch(asyncActionStart());
                  await getDoc(
                    listenToDocFromFirestore({
                      dbCollection: FIRESTORE_APPLICATIONS_COLLECTION,
                      id: formValue,
                    })
                  )
                    .then((doc) => {
                      if (doc.exists) {
                        setApplication({
                          ...doc.data(),
                          id: formValue,
                        });
                      } else {
                        handleNoResultsFound();
                      }
                      dispatch(asyncActionFinish());
                    })
                    .catch((error) => {
                      console.log(error);
                      throw error;
                    });
                }

                setSubmitting(false);
              } catch (error) {
                setErrors({ auth: error.message });
                dispatch(asyncActionFinish());
                setSubmitting(false);
              }
            }}
          >
            {() => (
              <Form className="ui form">
                <FormField>
                  <div className="transparent-text-wrapper">
                    <MyTransparentTextInput
                      className="transparent-text-input transparent-text-input-item"
                      name="formInput"
                      placeholder={`Search using ${lookupTypes[searchType]}`}
                      autoComplete="off"
                      value={formValue}
                      onChange={(e) => handleInputChange(e)}
                    />
                    <Button
                      style={{
                        padding: "1px 1px 1px 1px",
                        background: "white",
                        display: "inline",
                        height: 45,
                      }}
                      className="transparent-text-input-item clickable"
                      type="submit"
                    >
                      <div className="lookup-search-button">
                        <img
                          style={{ width: 33, height: 33 }}
                          src={searchIcon}
                          alt="search icon"
                        />
                      </div>
                    </Button>
                  </div>
                </FormField>
              </Form>
            )}
          </Formik>
        </Grid.Column>
        <Grid.Column
          verticalAlign="top"
          style={{ marginTop: 53, marginLeft: 5 }}
          width={3}
        >
          <Button
            className="overwrite-button cta-orange"
            content={"Clear"}
            onClick={handleClearButton}
          />
          {formError && (
            <Label
              basic
              color="grey"
              style={{ marginTop: 10 }}
              content="No results found"
            />
          )}
        </Grid.Column>
      </Grid.Row>
      {account && (
        <Grid.Row>
          <Grid.Column
            width={9}
            className="body-blue-text"
            style={{
              margin: "20px 0 5px",
              paddingBottom: 20,
              borderBottom: "1px solid orange",
            }}
          >
            <div style={{ marginBottom: 8 }}>
              <div className="body-bold-text">IDs: </div>
              <div>
                <span style={{ color: "#8f979f", marginRight: 8 }}>User:</span>
                {account.id}
              </div>
              <div>
                <span style={{ color: "#8f979f", marginRight: 8 }}>
                  SIB/Brevo:
                </span>
                {account.sibId}
              </div>
              {account.isEmployer && (
                <div>
                  <span style={{ color: "#8f979f", marginRight: 8 }}>
                    Company Id:
                  </span>
                  {account.companyId}
                </div>
              )}
            </div>
            {account.isEmployer && company && (
              <div style={{ marginBottom: 8 }}>
                <div className="body-bold-text">Company: </div>
                <div>
                  <span style={{ color: "#8f979f", marginRight: 8 }}>Name</span>{" "}
                  {company.name}
                </div>
                <div>
                  <span style={{ color: "#8f979f", marginRight: 8 }}>Id:</span>{" "}
                  {company?.id}
                </div>
                <div>
                  <span style={{ color: "#8f979f", marginRight: 8 }}>
                    Stripe:
                  </span>{" "}
                  {company?.stripeCustomerId}
                </div>
                <div>
                  <span
                    style={{
                      color: isCompanyOwnerAdmin ? "orange" : "#8f979f",
                      marginRight: 8,
                    }}
                  >
                    Owner & Administrator:
                  </span>{" "}
                  {company?.employerId}
                </div>
              </div>
            )}
            <div style={{ marginBottom: 8 }}>
              <span className="body-bold-text">Account Created: </span>
              <span>{getDateAndTime(account?.createdAt.toDate())}</span>
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="body-bold-text">Email: </span>
              <span>{account.email}</span>
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="body-bold-text">Phone: </span>
              <span>{account.phoneNumber}</span>
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="body-bold-text">Name: </span>
              <span>{account.displayName}</span>
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="body-bold-text">Type: </span>
              <span>
                {account.type === EMPLOYER_USER ? "Employer" : "Job Seeker"}
              </span>
              <span className="semi-bold accent-colour"> / </span>
              <span>Role: {account.role ?? "ADMIN_CHECK"}</span>
              <span>
                {account.type === EMPLOYER_USER &&
                  account.isRecruiter &&
                  " - Recruiter"}
              </span>
            </div>
            <div>
              <span className="body-bold-text">Verified: </span>
              <span>{account.isAccountVerified ? "Yes" : "No"}</span>
            </div>
          </Grid.Column>
          {isEmployer && (
            <Grid.Column width={7} style={{ margin: "20px 0 5px" }}>
              <div className="teal-box" style={{ width: "fit-content" }}>
                <span className="h3-text">Account Settings</span>
                <span>
                  <Checkbox
                    checked={isAccountSettingsLocked}
                    label="locked"
                    style={{
                      margin: "0 0 20px 10px",
                      paddingTop: 10,
                    }}
                    onChange={(e, { checked }) =>
                      setIsAccountSettingsLocked(checked)
                    }
                  />
                </span>
                <br />
                <UxToggle
                  isDisabled={isAccountSettingsLocked}
                  labelText="Hide location feature"
                  checked={account.isAllowHideLocation}
                  handleClick={async () =>
                    toggleSettingInDocument({
                      dbCollection: FIRESTORE_COMPANIES_COLLECTION,
                      id: company.id,
                      key: FIRESTORE_DOCUMENT_FIELDS.users.employer
                        .isAllowHideLocation,
                      toggle: company?.isAllowHideLocation,
                    })
                  }
                />
              </div>
              <DiscountMode isAdmin={true} isShowStepText={false} />
            </Grid.Column>
          )}
        </Grid.Row>
      )}
      {account && (
        <Accordion>
          <Accordion.Title
            style={{ width: "fit-content" }}
            active={isManageAccountAccordionOpen}
            index={0}
            onClick={() => setIsAuthAccordionOpen(!isAuthAccordionOpen)}
          >
            <Icon name="dropdown" />
            <span className="body-bold-text">Auth details</span>
          </Accordion.Title>
          <Accordion.Content active={isAuthAccordionOpen}>
            <div className={"padded"} style={{ width: "500px" }}>
              <pre>{authDetails}</pre>
            </div>
          </Accordion.Content>
        </Accordion>
      )}
      {isEmployer && (
        <>
          {companyProfile && (
            <Grid.Row>
              <Grid.Column width={15}>
                <Divider
                  dividerLine="1px solid #8d9cab"
                  margin={"30px 19% 20px 0"}
                />
                <h3>Company Profile</h3>
                <CompanyProfileListItem company={companyProfile} type={null} />
              </Grid.Column>
            </Grid.Row>
          )}
          <Grid.Row>
            <Grid.Column width={16}>
              <div className="body-blue-text clear-above">
                <span className="body-bold-text">Job REF:</span>
                <span> {refJob?.refNumber || ""} |</span>
                <span> {refJob?.title || ""} |</span>
                <span> {refJob ? getJobSubmittedDate(refJob) : ""}</span>
              </div>
              <br />
              <ManageJobPosts isAdmin={true} companyId={account.companyId} />
              <ManagePromotionServices
                isAdmin={true}
                companyId={account.companyId}
              />
            </Grid.Column>
          </Grid.Row>
        </>
      )}
      {isCandidate && (
        <Grid.Row>
          <Grid.Column width={7}>
            {candidateProfile?.isCompleted ? (
              <ProfileSuggestionCard profile={candidateProfile} />
            ) : (
              <div>Incomplete profile {candidateProfile?.id}</div>
            )}
          </Grid.Column>
        </Grid.Row>
      )}

      <Grid.Row>
        <Grid.Column>
          <Accordion>
            <Accordion.Title
              style={{ width: "fit-content" }}
              active={isManageAccountAccordionOpen}
              index={0}
              onClick={() =>
                setIsManageAccountAccordionOpen(!isManageAccountAccordionOpen)
              }
            >
              <Icon name="dropdown" />
              <span className="body-bold-text">Manage Account</span>
            </Accordion.Title>
            <Accordion.Content active={isManageAccountAccordionOpen}>
              <div style={{ paddingLeft: 15 }}>
                <div>
                  Before deleting this account, make sure:
                  <ul>
                    <li className="semi-bold" key={1}>
                      The SIB id matches the contact in the contacts list
                    </li>
                    <li className="semi-bold" key={2}>
                      Employer (owner & admin): Resolve subscription, balances,
                      outstanding invoices if any
                    </li>
                    <li className="semi-bold" key={3}>
                      Employer (owner & admin): Delete stripe customer manually
                    </li>
                    <li className="semi-bold" key={4}>
                      Employer (owner & admin): Check for subdomain on vercel
                      and delete
                    </li>
                    <li className="semi-bold" key={5}>
                      Employer (owner & admin): Delete company.
                    </li>
                    <li className="semi-bold" key={6}>
                      Employer (owner & admin): Check for company profile.
                      Delete profile, status log, metrics.
                    </li>
                    <li className="semi-bold" key={7}>
                      Employer: Remove userId from Users and Company.users and
                      remove Member object from Team. Remove from Brevo
                    </li>
                    <li className="semi-bold" key={8}>
                      Job Seeker: Profile files (CV, linkedin, pdfs etc) deleted
                    </li>
                  </ul>
                </div>
                <div>
                  After deleting this account, make sure:
                  <ul>
                    <li className="semi-bold" key={1}>
                      Account is deleted from the Authentication provider
                    </li>
                    <li className="semi-bold" key={2}>
                      User is deleted from Users
                    </li>
                  </ul>
                </div>
                <div>
                  <Checkbox
                    style={{ margin: "0 10px 0 0" }}
                    onChange={(e, { checked }) =>
                      handleConfirmDeleteToggleChange(e, { checked })
                    }
                  />
                  <span className="body-blue-text">
                    Confirm to delete user account
                  </span>
                </div>
                <Button
                  className={"overwrite-button blue-inverse-button"}
                  content={"Delete account"}
                  disabled={!isConfirmDelete || !account}
                  loading={isDeleting}
                  onClick={deleteLookupUser}
                  style={{ margin: "20px 0 0 0" }}
                />
              </div>
              {error && (
                <Label
                  basic
                  color="red"
                  style={{ marginBottom: 10, marginTop: 5 }}
                  content={error}
                />
              )}
            </Accordion.Content>
          </Accordion>
        </Grid.Column>
      </Grid.Row>

      {!!application && (
        <Grid.Row>
          <Divider dividerLine="1px solid #8d9cab" margin={"30px 19% 20px 0"} />
          <Grid.Column width={12}>
            <h3>
              Job: {application.jobTitle} | {application.jobRefNumber}
            </h3>
            <h4>
              {application.type} {application.id}
            </h4>
            <h4>
              AI ready: {application.aiReady ? "Yes" : "No"} | Admin reviewed:{" "}
              {application.isAdminReviewed ? "Yes" : "No"}
            </h4>
            <h4>Score: {application.score}/9</h4>
            <div
              style={{
                whiteSpace: "pre-line",
                border: "1px solid orange",
                borderRadius: 8,
                padding: 8,
              }}
            >
              {application.summary}
            </div>
            {application.aiSkillSet.skills.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div className="semi-bold">Skills</div>
                <Tags
                  tags={application.aiSkillSet.skills}
                  style={{ margin: "10px 0 10px 0" }}
                />
              </div>
            )}
            {application.aiSkillSet.softSkills.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div className="semi-bold">Soft skills</div>
                <Tags
                  tags={application.aiSkillSet.softSkills}
                  style={{ margin: "10px 0 10px 0" }}
                />
              </div>
            )}
            {application.aiSkillSet.languages.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div className="semi-bold">Languages</div>
                <Tags
                  tags={application.aiSkillSet.languages}
                  style={{ margin: "10px 0 10px 0" }}
                />
              </div>
            )}
            {application.interviewQuestions.length > 0 && (
              <div style={{ margin: "20px 0 10px" }}>
                <div className="semi-bold" style={{ marginBottom: 20 }}>
                  Interview questions
                </div>
                {application.interviewQuestions.map((q) => (
                  <div key={cuid()}> - {q}</div>
                ))}
              </div>
            )}
          </Grid.Column>
          <Grid.Column width={2}>
            <Button
              className="overwrite-button cta-orange"
              content={"Confirm"}
              onClick={handleConfirmApplication}
            />
          </Grid.Column>
        </Grid.Row>
      )}
    </Grid>
  );
}
